import { useRecoilCallback, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { DETAILS_PAGE_FLAVOUR_URL } from '../constants/coverable';
import { EndpointOpcConfig } from '../pages/OpcEntryPage/config/EndpointOpcConfig';
import { opcInfoAtom } from '../recoil/atoms/opcInfoAtom';
import useWizardNavigation from './useWizardNavigation';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import type { ProductName_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { OPCAttribute, OPCChangeType, OPCIdentifier } from '../recoil/types/opcTypes';
import type { AdjustCoverageAction } from '../pages/OpcEntryPage/types';
import type { JobInfoDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import { allCoverablesOnsaAtom } from '../recoil/atoms/allCoverablesOnsaAtom';
import { onsaProductStatusListAtom } from '../recoil/atoms/onsaProductStatusListAtom';
import { isOpcDataFilledAtom } from '../recoil/atoms/isOpcDataFilledAtom';
import { mpsActivationInfoAtom } from '../recoil/atoms/mpsActivationInfoAtom';
import { isOpcFetchingAtom } from '../recoil/atoms/isOpcFetchingAtom';
import { refreshCoverablesSelectorAtom } from '../recoil/atoms/refreshCoverablesSelectorAtom';
import { callMeVariantOpcAtom } from '../recoil/atoms/callMeVariantOpcAtom';

const usePageNavigation = (): {
    goBackToDetailsPage: () => void;
    goToShoppingBasket: (shouldRefreshData?: boolean, jobId?: string) => void;
    goToCheckoutPage: (shouldRefreshData?: boolean, jobId?: string) => void;
    goToOPCEditCoveragePage: (
        productName?: ProductName_Top,
        coverableId?: string,
        coverageId?: string,
        adjustAction?: AdjustCoverageAction,
        termIdOverride?: string,
        isFromDAFlow?: boolean
    ) => void;
    goToOPCEditAttributeModal: (
        productName?: ProductName_Top,
        coverableId?: string,
        attribute?: OPCAttribute,
        attributeValue?: string,
        isFromDAFlow?: boolean
    ) => void;
    goToOPCAddCoverageModal: (
        productName?: ProductName_Top,
        coverableId?: string,
        coverageId?: string,
        isFromDAFlow?: boolean
    ) => void;
    goToMPSActivationPage: (policyNumber: string) => void;
    goToAIAFlow: () => void;
    goToReceiptPage: () => void;
} => {
    const { coverableId, productName, tilpas, attribute, coverageId, adjustAction, termId } =
        useRecoilValue(opcInfoAtom);
    const { policyNumber } = useRecoilValue(jobInfoAtom);
    const jobInfo = useRecoilValue(jobInfoAtom);
    const resetOnsaCoverables = useSetRecoilState(allCoverablesOnsaAtom);
    const resetCoverables = useSetRecoilState(refreshCoverablesSelectorAtom);
    const resetIsOpcDataFilled = useResetRecoilState(isOpcDataFilledAtom);
    const resetIsOpcFetching = useResetRecoilState(isOpcFetchingAtom);
    const setOpcInfo = useSetRecoilState(opcInfoAtom);
    const setProductStatusList = useSetRecoilState(onsaProductStatusListAtom);
    const setMpsActivationInfo = useSetRecoilState(mpsActivationInfoAtom);
    const resetOpcCallMe = useResetRecoilState(callMeVariantOpcAtom);
    const searchParams = new URLSearchParams(location.search);

    // Handle batch update manually as default recoil batch update not working properly
    const batchUpdates = useRecoilCallback(
        ({ set }) =>
            (opcInfo: OPCIdentifier, jobInfo?: JobInfoDTO) => {
                wizardGotoStep('OpcEntryPage');
                set(opcInfoAtom, opcInfo);
                jobInfo && set(jobInfoAtom, jobInfo);
            }
    );

    const { jobNumber } = jobInfo;
    const { wizardGotoStep } = useWizardNavigation();

    const generateDetailsPageURL = (): string => {
        const subPath = EndpointOpcConfig.policyDetailPage;
        const flavour = DETAILS_PAGE_FLAVOUR_URL[productName];
        return `${subPath}${flavour}/?policeNummer=${policyNumber ?? ''}&id=${coverableId}`;
    };

    const generateAIAFlowURL = (): string => {
        const subPath = '/min-bil';
        return `${subPath}/?tilbudsId=${jobNumber ?? ''}&sourceFromMPS`;
    };

    const goBackToDetailsPage = (): void => {
        window.location.href = generateDetailsPageURL();
    };

    const goToAIAFlow = (): void => {
        window.location.href = generateAIAFlowURL();
    };

    const refreshDataAndUpdateUrlHistory = (
        shouldRefreshData: boolean = true,
        jobId?: string
    ): void => {
        window.history.pushState(
            {},
            '',
            `${window.location.pathname}?tilbudsId=${jobId ?? jobNumber}`
        );
        if (shouldRefreshData) {
            resetCoverables({});
        }
        resetOnsaCoverables({});
        setProductStatusList({});
        resetIsOpcDataFilled();
        resetIsOpcFetching();
        setOpcInfo((preInfo) => ({
            ...preInfo,
            coverageId: undefined,
            termId: undefined,
            adjustAction: undefined
        }));
    };

    const goToShoppingBasket = (shouldRefreshData: boolean = true, jobId?: string): void => {
        refreshDataAndUpdateUrlHistory(shouldRefreshData, jobId);
        wizardGotoStep('ShoppingBasketPage');
    };

    const goToCheckoutPage = (shouldRefreshData: boolean = true, jobId?: string): void => {
        refreshDataAndUpdateUrlHistory(shouldRefreshData, jobId);
        wizardGotoStep('CheckoutPage');
    };

    const goToReceiptPage = (): void => {
        searchParams.delete('confirmedToPay');
        searchParams.delete('mpsPoliceNummer');
        searchParams.set('tilbudsId', jobNumber);
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
        wizardGotoStep('ReceiptPage');
    };

    const updateOPCURL = (
        opcChangeType?: OPCChangeType,
        coverableIdOverride?: string,
        productNameOverride?: string,
        coverageIdOverride?: string,
        attributeOverride?: OPCAttribute
    ): void => {
        const queryParams = new URLSearchParams();
        queryParams.set('policeNummer', policyNumber ?? '');
        queryParams.set('tilpas', opcChangeType ?? tilpas);
        queryParams.set('coverableId', coverableIdOverride ?? coverableId);
        queryParams.set('productName', productNameOverride ?? productName);
        if (coverageIdOverride) {
            queryParams.set('coverageId', coverageIdOverride ?? coverageId);
        }
        if (attributeOverride) {
            queryParams.set('attribute', attributeOverride ?? attribute);
        }

        const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
        window.history.pushState({}, '', newUrl);
    };

    const updateMpsURL = (policeNummer: string): void => {
        const newUrl = `${window.location.pathname}?mpsPoliceNummer=${policeNummer}`;
        window.history.pushState({}, '', newUrl);
    };

    const goToOPCFlow = (
        opcChangeType: OPCChangeType,
        productNameOverride?: ProductName_Top,
        coverableIdOverride?: string,
        coverageIdOverride?: string,
        attributeOverride?: OPCAttribute,
        attributeValue?: string,
        adjustActionOverride?: AdjustCoverageAction,
        termIdOverride?: string,
        isFromDAFlow?: boolean
    ): void => {
        batchUpdates(
            {
                policeNummer: policyNumber ?? '',
                tilpas: opcChangeType ?? tilpas,
                productName: productNameOverride ?? productName,
                coverableId: coverableIdOverride ?? coverableId,
                coverageId: coverageIdOverride ?? coverageId,
                attribute: attributeOverride ?? attribute,
                attributeValue,
                adjustAction: adjustActionOverride ?? adjustAction,
                termId: termIdOverride ?? termId,
                isFromDAFlow
            },
            isFromDAFlow ? { ...jobInfo, isExistingBasket: false } : undefined
        );
        updateOPCURL(
            opcChangeType,
            coverableIdOverride,
            productNameOverride,
            coverageIdOverride,
            attributeOverride
        );
        resetOpcCallMe();
    };

    const goToMPSActivationPage = (policyNumber: string): void => {
        updateMpsURL(policyNumber);
        setMpsActivationInfo({
            policeNummer: policyNumber ?? '',
            isConfirmedToPay: false
        });
        wizardGotoStep('MPSActivationPage');
    };

    const goToOPCEditCoveragePage = (
        productName?: ProductName_Top,
        coverableId?: string,
        coverageId?: string,
        adjustAction?: AdjustCoverageAction,
        termId?: string,
        isFromDAFlow: boolean = true
    ): void => {
        goToOPCFlow(
            'tilpas',
            productName,
            coverableId,
            coverageId,
            undefined,
            undefined,
            adjustAction,
            termId,
            isFromDAFlow
        );
    };

    const goToOPCEditAttributeModal = (
        productName?: ProductName_Top,
        coverableId?: string,
        attribute?: OPCAttribute,
        attributeValue?: string,
        isFromDAFlow: boolean = true
    ): void => {
        goToOPCFlow(
            'attribut',
            productName,
            coverableId,
            undefined,
            attribute,
            attributeValue,
            undefined,
            undefined,
            isFromDAFlow
        );
    };

    const goToOPCAddCoverageModal = (
        productName?: ProductName_Top,
        coverableId?: string,
        coverageId?: string,
        isFromDAFlow: boolean = true
    ): void => {
        goToOPCFlow(
            'tilfoej',
            productName,
            coverableId,
            coverageId,
            undefined,
            undefined,
            undefined,
            undefined,
            isFromDAFlow
        );
    };
    return {
        goBackToDetailsPage,
        goToShoppingBasket,
        goToOPCEditCoveragePage,
        goToOPCAddCoverageModal,
        goToOPCEditAttributeModal,
        goToMPSActivationPage,
        goToAIAFlow,
        goToReceiptPage,
        goToCheckoutPage
    };
};

export default usePageNavigation;
